// Interactive sticker canvas (SVG, inches): material swatch, bleed, cut line, layers with move / scale / rotate handles, zoom + pan.
import { useEffect, useRef, useState } from "react";
import { cutLoops, loopsPath, tileBox, layerBox } from "./stickerShapes";
import { MaterialDefs, materialFill, materialFilter, sheenOpacity } from "./StickerMaterial";
import { LayerView } from "./LayerView";
import { materialOf } from "./stickerModel";

const HANDLES = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
const rad = (d) => (d * Math.PI) / 180;

export const StickerCanvas = ({ doc, sel, setSel, change, commit, showCut = true, zoom, setZoom }) => {
  const wrap = useRef(null), svgRef = useRef(null), drag = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [guide, setGuide] = useState(null);
  useEffect(() => {
    const el = wrap.current; if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el); setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);
  const tile = tileBox(doc), pad = 0.9;
  const x0 = Math.min(0, tile.x) - pad, y0 = Math.min(0, tile.y) - pad, x1 = Math.max(doc.w, tile.x + tile.w) + pad, y1 = Math.max(doc.h, tile.y + tile.h) + pad;
  const fitScale = Math.min(size.w / (x1 - x0), size.h / (y1 - y0)) || 1;
  const scale = fitScale * zoom;
  const vw = size.w / scale, vh = size.h / scale;
  const vx = (x0 + x1) / 2 - vw / 2 + pan.x, vy = (y0 + y1) / 2 - vh / 2 + pan.y;
  const toInch = (e) => { const r = svgRef.current.getBoundingClientRect(); return { x: vx + (e.clientX - r.left) / scale, y: vy + (e.clientY - r.top) / scale }; };
  const layer = doc.layers.find((l) => l.id === sel);

  const begin = (e, mode, l) => {
    e.stopPropagation(); e.preventDefault();
    svgRef.current.setPointerCapture(e.pointerId);
    const pt = toInch(e);
    drag.current = { mode, start: pt, startClient: { x: e.clientX, y: e.clientY }, scale, orig: l ? { ...l } : null, pan0: pan, a0: l ? Math.atan2(pt.y - l.y, pt.x - l.x) : 0, d0: l ? Math.hypot(pt.x - l.x, pt.y - l.y) : 1, moved: false };
  };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    if (d.mode === "pan") {
      const dx = e.clientX - d.startClient.x, dy = e.clientY - d.startClient.y;
      d.moved = d.moved || Math.hypot(dx, dy) > 3;
      return setPan({ x: d.pan0.x - dx / d.scale, y: d.pan0.y - dy / d.scale });
    }
    const pt = toInch(e); d.moved = d.moved || Math.hypot(pt.x - d.start.x, pt.y - d.start.y) > 0.01;
    const o = d.orig;
    if (d.mode === "move") {
      let nx = o.x + pt.x - d.start.x, ny = o.y + pt.y - d.start.y;
      const g = {};
      if (Math.abs(nx - doc.w / 2) < 0.06) { nx = doc.w / 2; g.x = true; }
      if (Math.abs(ny - doc.h / 2) < 0.06) { ny = doc.h / 2; g.y = true; }
      setGuide(g.x || g.y ? g : null);
      change(o.id, { x: nx, y: ny });
    } else if (d.mode === "scale") {
      const k = Math.max(0.05, Math.hypot(pt.x - o.x, pt.y - o.y) / d.d0);
      change(o.id, o.kind === "text" ? { size: o.size * k } : { w: o.w * k, h: o.h * k });
    } else if (d.mode === "rotate") {
      let deg = o.rot + ((Math.atan2(pt.y - o.y, pt.x - o.x) - d.a0) * 180) / Math.PI;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15;
      change(o.id, { rot: ((deg % 360) + 360) % 360 });
    }
  };
  const onUp = (e) => {
    const d = drag.current; if (!d) return;
    drag.current = null; setGuide(null);
    try { svgRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (d.mode === "pan") { if (!d.moved) setSel(null); return; }
    if (d.moved) commit();
  };
  const onWheel = (e) => { e.preventDefault(); setZoom((z) => Math.max(0.3, Math.min(8, z * (e.deltaY < 0 ? 1.12 : 1 / 1.12)))); };
  useEffect(() => { const el = svgRef.current; if (!el) return; el.addEventListener("wheel", onWheel, { passive: false }); return () => el.removeEventListener("wheel", onWheel); }); // eslint-disable-line react-hooks/exhaustive-deps

  const uid = "stk", mat = materialOf(doc), loops = cutLoops(doc), cutD = loopsPath(loops);
  const bg = doc.bg || (mat.opaque ? (mat.bg.startsWith("#") ? mat.bg : "#ffffff") : null);
  const hs = 7 / scale, box = layer ? layerBox(layer) : null;
  return (
    <div ref={wrap} className="relative h-full w-full overflow-hidden bg-[#1a1f2b]" data-testid="sticker-canvas" style={{ backgroundImage: "radial-gradient(#2b3242 0.8px, transparent 0.8px)", backgroundSize: "18px 18px" }}>
      <svg ref={svgRef} width={size.w} height={size.h} viewBox={`${vx} ${vy} ${vw} ${vh}`} onPointerDown={(e) => begin(e, "pan")} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} className="block touch-none select-none">
        <MaterialDefs id={uid} />
        {/* a swatch of the material under the sticker */}
        <rect x={tile.x - 0.5} y={tile.y - 0.5} width={tile.w + 1} height={tile.h + 1} rx={0.15} fill={materialFill(doc, uid)} filter={materialFilter(doc, uid)} />
        <rect x={tile.x - 0.5} y={tile.y - 0.5} width={tile.w + 1} height={tile.h + 1} rx={0.15} fill={`url(#${uid}-sheen)`} opacity={sheenOpacity(doc)} style={{ pointerEvents: "none" }} />
        {/* bleed (grown cut line) then the sticker fill */}
        {bg && doc.bleed > 0 && <path d={cutD} fill={bg} stroke={bg} strokeWidth={doc.bleed * 2} strokeLinejoin="round" opacity={0.55} />}
        {bg && <path d={cutD} fill={bg} />}
        {doc.layers.map((l) => <LayerView key={l.id} l={l} onPointerDown={(e) => { setSel(l.id); begin(e, "move", l); }} />)}
        {doc.border?.width > 0 && <path d={cutD} fill="none" stroke={doc.border.color} strokeWidth={doc.border.width * 2} clipPath={undefined} style={{ pointerEvents: "none" }} />}
        {/* design box, cut line, bleed line */}
        <rect x={0} y={0} width={doc.w} height={doc.h} fill="none" stroke="#7a86a3" strokeWidth={1 / scale} strokeDasharray={`${4 / scale} ${4 / scale}`} opacity={0.5} style={{ pointerEvents: "none" }} />
        {showCut && <path d={cutD} fill="none" stroke="#ff00c8" strokeWidth={1.5 / scale} strokeDasharray={`${6 / scale} ${4 / scale}`} style={{ pointerEvents: "none" }} data-testid="sticker-cut-line" />}
        {showCut && doc.bleed > 0 && <path d={cutD} fill="none" stroke="#9aa3b5" strokeWidth={doc.bleed * 2} opacity={0.12} strokeLinejoin="round" style={{ pointerEvents: "none" }} />}
        {guide?.x && <line x1={doc.w / 2} x2={doc.w / 2} y1={y0} y2={y1} stroke="#4c9ffe" strokeWidth={1 / scale} />}
        {guide?.y && <line y1={doc.h / 2} y2={doc.h / 2} x1={x0} x2={x1} stroke="#4c9ffe" strokeWidth={1 / scale} />}
        {layer && box && (
          <g transform={`translate(${layer.x} ${layer.y}) rotate(${layer.rot || 0})`} data-testid="sticker-selection">
            <rect x={-box.w / 2} y={-box.h / 2} width={box.w} height={box.h} fill="none" stroke="#4c9ffe" strokeWidth={1.2 / scale} />
            <line x1={0} y1={-box.h / 2} x2={0} y2={-box.h / 2 - 22 / scale} stroke="#4c9ffe" strokeWidth={1 / scale} />
            <circle cx={0} cy={-box.h / 2 - 22 / scale} r={hs} fill="#121824" stroke="#4c9ffe" strokeWidth={1.5 / scale} style={{ cursor: "grab" }} onPointerDown={(e) => begin(e, "rotate", layer)} data-testid="sticker-rotate-handle" />
            {HANDLES.map(([sx, sy], i) => <rect key={i} x={(sx * box.w) / 2 - hs} y={(sy * box.h) / 2 - hs} width={hs * 2} height={hs * 2} fill="#121824" stroke="#4c9ffe" strokeWidth={1.5 / scale} style={{ cursor: sx * sy > 0 ? "nwse-resize" : "nesw-resize" }} onPointerDown={(e) => begin(e, "scale", layer)} />)}
          </g>
        )}
      </svg>
      <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/50 px-2 py-1 font-mono text-[10px] text-[#9aa0a8]">{Math.round(scale)} px/in · {Math.round(zoom * 100)}%</div>
      <div className="absolute bottom-2 right-2 flex gap-1">
        {[["−", () => setZoom((z) => Math.max(0.3, z / 1.25))], ["Fit", () => { setZoom(1); setPan({ x: 0, y: 0 }); }], ["+", () => setZoom((z) => Math.min(8, z * 1.25))]].map(([t, fn]) => (
          <button key={t} onClick={fn} className="rounded bg-black/60 px-2 py-1 text-[11px] text-white hover:bg-black/80" data-testid={`sticker-zoom-${t === "−" ? "out" : t === "+" ? "in" : "fit"}`}>{t}</button>
        ))}
      </div>
      <div className="pointer-events-none absolute left-2 top-2 flex items-center gap-2 rounded bg-black/50 px-2 py-1 text-[10px] text-[#c7cbd1]">
        <span className="inline-block h-2 w-5 border-t-2 border-dashed border-[#ff00c8]" /> cut line <span className="ml-2 inline-block h-2 w-5 border-t-2 border-dashed border-[#7a86a3]" /> design area
      </div>
    </div>
  );
};
